import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import {
  Network, Layers, ArrowUpFromLine, ArrowDownToLine,
  Shield, Lock, FileText, Briefcase, Users, CheckCircle2,
  Activity, ListChecks, Database, GitBranch, Building2
} from 'lucide-react';

const FinancialIntegrationPage = () => {
  const headerStats = [
    { metric: '12+', label: 'Federal financial systems AcuSightPro is bridge-ready to integrate with' },
    { metric: 'ICD', label: 'Standards-based interface contracts with agency program offices' },
    { metric: '$0', label: 'Replacement cost. We enhance, never replace' },
    { metric: 'FedRAMP', label: 'Moderate authorization path' }
  ];

  const principles = [
    {
      icon: Building2,
      title: 'Your System of Record Stays. We Sit Above It.',
      description: 'AcuSightPro is the intelligence layer above the agency\'s existing financial system of record. The prime systems integrator, the interface developer, and the program office all stay in their established roles. We add the AI engine that turns scattered inputs into clean, compliant acquisition packages.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: ArrowUpFromLine,
      title: 'Clean Packages In. Real Money Out.',
      description: 'Finished acquisition packages from AcuSightPro push directly into the agency\'s acquisition management system through the published interface framework. Contracting officers review in their existing tools, approve, and the rest of the lifecycle runs through the established financial workflow the way it does today.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: ArrowDownToLine,
      title: 'Outcomes Flow Back. ACI Learns.',
      description: 'Awards, modifications, protests, and closeouts flow from the system of record back into AcuSightPro. Our compound intelligence engine learns from every real procurement outcome and gets smarter for every agency over time.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'Compliant by Construction.',
      description: 'Every push is validated against the published interface schema before it touches the system of record. Every transaction is encrypted in transit, audit-logged, and traceable to the AcuSightPro user, the source documents, and the policy rules that produced it.',
      color: 'from-orange-500 to-red-600'
    }
  ];

  const tiers = [
    {
      tier: 'Tier 0',
      title: 'Blueprint-Ready Pattern',
      timeline: 'Architecture in hand',
      description: 'AcuSightPro has already designed the integration framework for VA iFAMS, including transaction schema, field mapping, audit logging, and validation gates. The architecture is portable. Once a federal program office grants AcuSightPro an interface partner role and access to the published interface specification, the same pattern applies to any system of record. This is a designed and blueprint-ready reference, not a claim of formal partnership, certified interface, or current production access.',
      bullets: [
        'Reference pattern: VA iFAMS interface framework (designed, not in production)',
        'Transaction schema, field mapping, and audit log structure already drafted',
        'Portable to any agency system of record once interface partner status is granted',
        'Zero replacement risk: the agency keeps its prime, its integrator, and its system of record'
      ]
    },
    {
      tier: 'Tier 1',
      title: 'Read-Only Intelligence Layer',
      timeline: '~90 days',
      description: 'AcuSightPro reads historical procurement data from the agency\'s system of record via approved data feeds. ACI uses the data to train compound intelligence across agencies. Users do all work in AcuSightPro and manually export finished packages using existing workflows. No FedRAMP blocker.',
      bullets: [
        'No production write access to the system of record',
        'Runs inside the agency boundary as an experimental integration',
        'MOA-driven pilot scope',
        'Fastest path to operational value'
      ]
    },
    {
      tier: 'Tier 2',
      title: 'One-Way Push',
      timeline: '6\u20139 months',
      description: 'Finished acquisition packages push directly into the system of record as draft transactions. The contracting officer reviews in the agency\'s existing tools, approves, and the established prime + interface developer route the rest of the way through normal channels. Requires MOA + assigned interface partner ID.',
      bullets: [
        'AcuSightPro packages render as draft transactions in the system of record',
        'Interface field mapping locked, validated, and version-controlled',
        'Audit trail tied to AcuSightPro user identity',
        'Surfaces compliance gaps before they touch the system of record'
      ]
    },
    {
      tier: 'Tier 3',
      title: 'Bidirectional Sync',
      timeline: '12\u201318 months',
      description: 'The system of record sends outcome data back to AcuSightPro. ACI learns from real awards, modifications, protest outcomes, and closeouts. The compound intelligence model gets smarter for every agency simultaneously. Requires FedRAMP Moderate and production ATO.',
      bullets: [
        'Real outcome learning powers cross-agency benchmarks',
        'Anomaly detection on modification patterns',
        'Protest-risk scoring tuned by historical results',
        'Long-term moat: the more agencies, the smarter the model'
      ]
    }
  ];

  const roles = [
    {
      icon: Briefcase,
      party: 'Agency Program Office',
      role: 'Program owner. Grants interface partner ID, interface specification access, MOA, and ATO sponsorship.'
    },
    {
      icon: Network,
      party: 'Prime Systems Integrator',
      role: 'Operates the agency\'s financial system of record. Reviews the technical handshake and approves the data contract.'
    },
    {
      icon: GitBranch,
      party: 'Interface Developer',
      role: 'Builds and maintains the interface gateway between AcuSightPro and the agency\'s system of record.'
    },
    {
      icon: Database,
      party: 'AcuSightPro \u00b7 Encore Services',
      role: 'Intelligence layer + AI engine. Generates clean compliant packages, pushes via the gateway, learns from inbound outcomes.'
    }
  ];

  const compliancePoints = [
    { label: 'Interface Specification', value: 'Standards-based, agency-owned interface contract' },
    { label: 'Authentication', value: 'SAML 2.0 single sign-on + mTLS client certificates' },
    { label: 'Transport encryption', value: 'TLS 1.3 with strict HSTS' },
    { label: 'Data-at-rest encryption', value: 'AES-256, managed by the underlying cloud service' },
    { label: 'Authorization to Operate', value: 'Tier 1 inside agency boundary. Tier 2/3 targets FedRAMP Moderate.' },
    { label: 'Audit trail', value: 'Every push, validation, and inbound event timestamped, user-attributed, payload-hashed.' },
    { label: 'Data residency', value: 'AWS GovCloud where the underlying service operates.' },
    { label: 'Continuous monitoring', value: 'NIST SP 800-137 ConMon plan aligned with agency program office requirements.' }
  ];

  const fieldMappings = [
    { acusightpro: 'acquisition.title', system: 'acquisition_header.short_title' },
    { acusightpro: 'acquisition.naics', system: 'acquisition_header.naics_code' },
    { acusightpro: 'acquisition.psc', system: 'acquisition_header.psc_code' },
    { acusightpro: 'acquisition.set_aside', system: 'acquisition_header.set_aside_type' },
    { acusightpro: 'acquisition.igce.total', system: 'acquisition_financial.estimated_value' },
    { acusightpro: 'acquisition.fund_cite', system: 'financial_allocation.appropriation' },
    { acusightpro: 'acquisition.contracting_officer', system: 'acquisition_header.ko_pid' },
    { acusightpro: 'acquisition.requirements_doc', system: 'acquisition_documents.sow_blob' },
    { acusightpro: 'acquisition.evaluation_plan', system: 'evaluation_plan.scoring_matrix' },
    { acusightpro: 'acquisition.clauses', system: 'acquisition_clauses.applicable_clauses' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                  Financial Systems Integration
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  The Intelligence Layer
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                    Above Your System of Record.
                  </span>
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
                  AcuSightPro is not a replacement for your agency&rsquo;s financial system of record. We enhance established federal financial and acquisition management platforms through standards-based, agency-owned interface contracts.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Clean, FAR / VAAR-compliant acquisition packages flow into the system of record. Awards, modifications, and closeouts flow back. Compound intelligence learns from every real procurement outcome and makes the next cycle smarter.
                </p>

                {/* Critical Disclosure: integrations are custom-coded per request, no partnerships */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <div className="font-bold text-amber-900 text-base mb-1">Important: Custom-Coded, Authorization-Required</div>
                      <p className="text-sm text-amber-900 leading-relaxed">
                        All integrations and bridges shown on this page are <strong>custom-coded on a per-request basis</strong> and only after the agency program office has granted Encore Services formal interface partner status and access to the published interface specification. No platform listed here is an active production integration, and no claim of partnership, endorsement, or certified interface with any vendor or agency is made or implied.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="/contact" className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-full shadow-lg hover:opacity-90 transition-all text-center">
                    Talk to the Team
                  </a>
                  <a href="/tech-security" className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-indigo-50 hover:border-purple-600 transition-all text-center">
                    See Tech &amp; Security
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {headerStats.map((s) => (
                  <div key={s.label} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
                    <div className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 mb-1">{s.metric}</div>
                    <div className="text-sm text-gray-600 leading-snug">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Four Principles */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-4">
                How It Works
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Four Principles, One Architecture
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                AcuSightPro is built to live alongside the federal systems already serving the mission. We add intelligence, never overlap.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {principles.map((p) => (
                <div key={p.title} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-7 hover:shadow-lg transition-all">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-4`}>
                    <p.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{p.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Federal Financial Systems We Can Bridge */}
        <section className="py-20 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
                Bridge-Ready Systems
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Federal Financial Systems We Can Bridge
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                AcuSightPro is built on a standards-based integration framework. Once a federal program office grants interface partner access and the published interface specification, AcuSightPro can build a compliant bridge to any of the following systems of record. No replacement. No overlap. The agency&rsquo;s prime, integrator, and program office stay in their lanes.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { name: 'VA iFAMS', org: 'Department of Veterans Affairs', note: 'Reference pattern. Architecture already designed.' },
                { name: 'Oracle E-Business Suite', org: 'DOE / NREL and other federal labs', note: 'Standards-based interface contract. Oracle financials adapter.' },
                { name: 'SAP ECC and S/4HANA', org: 'IRS, Treasury bureaus, Navy ERP', note: 'SAP financials adapter via published API and IDoc framework.' },
                { name: 'GFEBS', org: 'U.S. Army', note: 'General Fund Enterprise Business System. SAP-based.' },
                { name: 'DAI', org: 'Defense Agencies Initiative', note: 'DLA, DCMA, OSD, and 25+ defense agencies on Oracle Federal Financials.' },
                { name: 'DEAMS', org: 'U.S. Air Force', note: 'Defense Enterprise Accounting and Management System.' },
                { name: 'Navy ERP', org: 'Department of the Navy', note: 'SAP-based enterprise financial platform.' },
                { name: 'Momentum', org: 'FBI, DOJ, HHS, USDA, Treasury bureaus', note: 'CGI Federal financial management platform.' },
                { name: 'PRISM', org: 'DHS, DOI, OPM, and others', note: 'Compusearch acquisition management platform.' },
                { name: 'FPDS-NG', org: 'Federal Procurement Data System', note: 'Government-wide procurement reporting. Live integration in AcuSightPro.' },
                { name: 'SAM.gov', org: 'System for Award Management', note: 'Federal entity registry and opportunity feed. Live integration.' },
                { name: 'USASpending.gov', org: 'Treasury / OMB', note: 'Federal spending transparency. Live integration.' }
              ].map((sys) => (
                <div key={sys.name} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-5 hover:shadow-md hover:border-purple-300 transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Database className="w-4 h-4 text-purple-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 text-base mb-0.5">{sys.name}</div>
                      <div className="text-xs text-gray-500 mb-2 leading-snug">{sys.org}</div>
                      <div className="text-sm text-gray-600 leading-snug">{sys.note}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Not a Partnership Disclosure */}
            <div className="mt-10 bg-red-50 border-2 border-red-300 rounded-2xl p-6">
              <div className="flex items-start gap-4 max-w-4xl mx-auto">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-red-700" />
                </div>
                <div>
                  <div className="font-bold text-red-900 text-base mb-2">Not a Partnership. Not a Production Integration. Custom-Coded Per Request.</div>
                  <p className="text-sm text-red-900 leading-relaxed mb-2">
                    The systems listed above are not partners, endorsers, or affiliated vendors of Encore Services or AcuSightPro. Each system named is a bridge candidate that can be custom-coded by Encore Services <strong>only if and when the relevant agency program office grants formal interface partner status, access to the published interface specification, and a signed Memorandum of Agreement covering interface scope, data handling, and security controls</strong>.
                  </p>
                  <p className="text-sm text-red-900 leading-relaxed">
                    The VA iFAMS reference pattern is an internal Encore Services architectural design exercise, not a production integration, not an iFAMS-approved interface, and not a statement of any current partnership with the Department of Veterans Affairs or the iFAMS program office.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 text-center">
              <p className="text-sm text-gray-700 leading-relaxed max-w-3xl mx-auto">
                <span className="font-semibold text-gray-900">Don&rsquo;t see your system of record listed?</span> AcuSightPro&rsquo;s integration framework is built to API-first, modular open systems principles. If your platform exposes a published interface specification and the agency authorizes the work, Encore Services can custom-code a bridge under the same MOA and ICD model. <a href="/contact" className="text-purple-700 font-semibold hover:underline">Talk to the team</a>.
              </p>
            </div>
          </div>
        </section>

        {/* Three-Tier Integration Ladder */}
        <section className="py-20 bg-gradient-to-b from-indigo-50/40 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-semibold rounded-full mb-4">
                Integration Ladder
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Three Tiers. Each Earns the Next.
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We don&rsquo;t ask agencies for production write access on day one. Trust is built tier by tier, with the program office in command at every step.
              </p>
            </div>
            <div className="space-y-6">
              {tiers.map((t) => (
                <div key={t.tier} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all">
                  <div className="grid lg:grid-cols-[200px_1fr] gap-6 items-start">
                    <div>
                      <div className="inline-block px-3 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold rounded-full mb-2">
                        {t.tier}
                      </div>
                      <div className="text-2xl font-bold text-gray-900">{t.title}</div>
                      <div className="text-sm font-mono text-gray-500 mt-1">{t.timeline}</div>
                    </div>
                    <div>
                      <p className="text-gray-700 leading-relaxed mb-4">{t.description}</p>
                      <ul className="space-y-2">
                        {t.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-3 text-sm text-gray-600">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who Does What */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
                Roles &amp; Responsibilities
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Everyone Stays in Their Lane
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Production integration runs as a multi-party arrangement. AcuSightPro generates demand for new interfaces. We never replace an incumbent.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {roles.map((r) => (
                <div key={r.party} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <r.icon className="w-5 h-5 text-indigo-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">{r.party}</div>
                    <div className="text-sm text-gray-600 leading-relaxed">{r.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Field Mapping */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
              <div>
                <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
                  Field Mapping
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Every Field Has a Documented Destination
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  AcuSightPro field names map to the destination schema documented in the agency-owned interface specification. No drift. No re-keying. No shadow systems.
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Sample mappings below. Complete interface specifications include the full agency field contract maintained by the program office.
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-3 text-white text-xs uppercase tracking-widest font-bold flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Sample Mappings
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-5 py-2.5 font-semibold text-gray-700">AcuSightPro</th>
                        <th className="text-left px-5 py-2.5 font-semibold text-gray-700">System of Record</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fieldMappings.map((f, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-5 py-2 font-mono text-xs text-blue-700">{f.acusightpro}</td>
                          <td className="px-5 py-2 font-mono text-xs text-emerald-700">{f.system}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance & Security */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
                Compliance &amp; Security
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Built to Pass the Same Bar as the System of Record
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                AcuSightPro meets the federal standards established financial platforms already meet. No exceptions, no shortcuts.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
              {compliancePoints.map((c) => (
                <div key={c.label} className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-xl p-5 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700 mb-1">{c.label}</div>
                    <div className="text-sm text-gray-700">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-purple-600 via-indigo-700 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to See Financial Integration in Action?
            </h2>
            <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
              We&rsquo;ll walk your acquisition leadership and OIT counterparts through the demo-mode integration, the interface field mapping, and the three-tier rollout plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="px-8 py-3 bg-white text-indigo-700 font-semibold rounded-full shadow-lg hover:opacity-90 transition-all">
                Schedule a Walkthrough
              </a>
              <a href="/product" className="px-8 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-indigo-700 transition-all">
                Explore the Platform
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FinancialIntegrationPage;
